// src/pages/Subscribe.jsx
import { useState } from 'react';
import { authFetch } from '../lib/authFetch';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Card, CardHeader, CardTitle, CardContent, CardDescription
} from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import {
  Check, Zap, Crown, Users, ArrowRight,
  Loader2, Settings, AlertCircle, Clock, ShieldCheck,
} from 'lucide-react';
import toast from 'react-hot-toast';

const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    price: 15,
    icon: Zap,
    iconColor: 'text-blue-500',
    description: 'For solo publishers and ecommerce operators',
    cta: 'Get Starter',
    features: [
      '1 Shopify store',
      'Meta Ads, Google Ads & TikTok Ads',
      'Google AdSense & Ad Manager',
      'Daily spend & revenue reports',
      'Export CSV',
      { shield: true, text: 'HapiGuard: Invalid click protection' },
      { shield: true, text: 'Blocked visitor details (country, device, OS)' },
      { shield: true, text: 'Country geo-blocking' },
      'Email support',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 30,
    icon: Crown,
    iconColor: 'text-yellow-500',
    popular: true,
    description: 'Everything in Starter, plus advanced analytics and protection',
    cta: 'Get Pro',
    features: [
      '1 Shopify store',
      'Everything in Starter',
      'Marketing attribution',
      'Customer LTV & cohorts',
      'Product P&L breakdown',
      'Landing page builder & A/B testing',
      { shield: true, text: 'Block by device ID, OS & IP address' },
      { shield: true, text: 'Anomaly detection & email alerts' },
      { shield: true, text: 'Advanced geo allow-list' },
      'Priority support',
    ],
  },
  {
    id: 'business',
    name: 'Business',
    price: 69,
    icon: Users,
    iconColor: 'text-purple-500',
    description: 'For teams and multi-store operators',
    cta: 'Get Business',
    features: [
      '3 Shopify stores',
      'Everything in Pro',
      'Up to 2 team members',
      'Team role management',
      'Centralised billing',
      'Multi-store analytics',
      { shield: true, text: 'Multi-site HapiGuard management' },
      { shield: true, text: 'All protection features' },
      'Priority + phone support',
    ],
  },
];

export default function Subscribe() {
  const navigate    = useNavigate();
  const { user, profile, isAdmin, isTrialActive, isTrialExpired, daysRemaining } = useAuth();
  const [loading, setLoading]       = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const currentTier = profile?.tier || 'trial';

  const handleSubscribe = async (planId) => {
    setLoading(true);
    setSelectedPlan(planId);
    const tid = toast.loading('Setting up your subscription...');
    try {
      const res  = await authFetch('/api/billing', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'create_checkout', userId: user.id, planId }),
      });
      const data = await res.json();
      if (data.success && data.checkoutUrl) {
        toast.dismiss(tid);
        window.location.href = data.checkoutUrl;
      } else {
        toast.error(data.error || 'Failed to start subscription', { id: tid });
        setLoading(false);
      }
    } catch (e) { toast.error('Failed to start subscription', { id: tid }); setLoading(false); }
  };

  const handleManage = async () => {
    setLoading(true);
    const tid = toast.loading('Opening billing portal...');
    try {
      const res  = await authFetch('/api/billing', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'portal', userId: user.id }),
      });
      const data = await res.json();
      if (data.success && data.portalUrl) { toast.dismiss(tid); window.location.href = data.portalUrl; }
      else { toast.error(data.error || 'Failed to open billing portal', { id: tid }); setLoading(false); }
    } catch (e) { toast.error('Failed to open portal', { id: tid }); setLoading(false); }
  };

  if (isAdmin) {
    return (
      <div className="max-w-2xl mx-auto py-12 text-center">
        <Crown className="h-16 w-16 mx-auto mb-4 text-primary" />
        <h1 className="text-2xl font-bold mb-2">Admin Access</h1>
        <p className="text-muted-foreground mb-6">As an admin, you have full access to all features.</p>
        <Button onClick={() => navigate('/dashboard')}>Go to Overview</Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="text-center mb-10">
        <h1 className="text-3xl font-bold mb-3">Choose your plan</h1>
        <p className="text-lg text-muted-foreground">Simple, transparent pricing. No hidden fees.</p>

        {isTrialActive && (
          <div className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium">
            <Clock className="h-4 w-4" />
            {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} left in your trial
          </div>
        )}
        {isTrialExpired && (
          <div className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full bg-red-100 text-red-700 text-sm font-medium">
            <AlertCircle className="h-4 w-4" /> Your trial has expired — subscribe to continue
          </div>
        )}
        {currentTier === 'business' && (
          <div className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full bg-purple-100 text-purple-700 text-sm font-medium">
            <Users className="h-4 w-4" /> You're on the Business plan
            <Button variant="outline" size="sm" className="ml-2" onClick={handleManage} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Settings className="h-4 w-4 mr-1" />} Manage
            </Button>
          </div>
        )}
        {currentTier === 'pro' && (
          <div className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full bg-yellow-100 text-yellow-700 text-sm font-medium">
            <Crown className="h-4 w-4" /> You're on the Pro plan
            <Button variant="outline" size="sm" className="ml-2" onClick={handleManage} disabled={loading}>
              <Settings className="h-4 w-4 mr-1" /> Manage
            </Button>
          </div>
        )}
        {currentTier === 'starter' && (
          <div className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full bg-blue-100 text-blue-700 text-sm font-medium">
            <Zap className="h-4 w-4" /> You're on the Starter plan — upgrade for more features
          </div>
        )}
      </div>

      {/* HapiGuard callout */}
      <div className="mb-8 rounded-2xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/20 p-5 flex items-start gap-4">
        <ShieldCheck className="h-7 w-7 text-emerald-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-emerald-900 dark:text-emerald-300 mb-0.5">
            Every paid plan includes HapiGuard click protection
          </p>
          <p className="text-sm text-emerald-800 dark:text-emerald-400">
            Protect your AdSense and Ad Manager account from invalid clicks and click bombing.
            Install the free WordPress plugin, connect your account, and HapiGuard handles the rest.
            Starter gets country blocking. Pro adds device ID, OS &amp; IP blocking with email alerts.
          </p>
        </div>
      </div>

      {/* Plan cards */}
      <div className="grid md:grid-cols-3 gap-6 mb-12">
        {PLANS.map(plan => {
          const isCurrent  = currentTier === plan.id;
          const tierOrder  = { trial: 0, starter: 1, pro: 2, business: 3 };
          const isUpgrade  = tierOrder[currentTier] < tierOrder[plan.id];
          const isDowngrade= tierOrder[currentTier] > tierOrder[plan.id];
          const Icon = plan.icon;

          return (
            <Card key={plan.id} className={`relative flex flex-col ${plan.popular ? 'border-primary shadow-lg' : ''} ${isCurrent ? 'bg-primary/5' : ''}`}>
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-primary text-primary-foreground px-3 py-1">Most popular</Badge>
                </div>
              )}

              <CardHeader className="text-center pb-2">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Icon className={`h-6 w-6 ${plan.iconColor}`} />
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                </div>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold">${plan.price}</span>
                  <span className="text-muted-foreground">/month</span>
                </div>
                <p className="text-sm text-muted-foreground mt-2">{plan.description}</p>
              </CardHeader>

              <CardContent className="flex flex-col flex-1 space-y-6">
                <ul className="space-y-2 flex-1">
                  {plan.features.map((f, i) => {
                    const isShield = typeof f === 'object' && f.shield;
                    const text     = isShield ? f.text : f;
                    return (
                      <li key={i} className="flex items-start gap-2.5">
                        {isShield
                          ? <ShieldCheck className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                          : <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />}
                        <span className={`text-sm ${isShield ? 'text-emerald-700 dark:text-emerald-400 font-medium' : ''}`}>
                          {text}
                        </span>
                      </li>
                    );
                  })}
                </ul>

                <Button
                  className="w-full"
                  variant={plan.popular ? 'default' : 'outline'}
                  size="lg"
                  onClick={() => isCurrent ? handleManage() : handleSubscribe(plan.id)}
                  disabled={loading || isDowngrade}
                >
                  {loading && selectedPlan === plan.id ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" />Processing...</>
                  ) : isCurrent ? (
                    <><Settings className="h-4 w-4 mr-2" />Manage plan</>
                  ) : isDowngrade ? (
                    'Contact support to downgrade'
                  ) : (
                    <>{plan.cta} <ArrowRight className="h-4 w-4 ml-2" /></>
                  )}
                </Button>

                {isCurrent && <p className="text-center text-xs text-muted-foreground">Your current plan</p>}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Feature comparison note for Business */}
      <div className="bg-accent rounded-2xl p-6 mb-10 flex flex-col sm:flex-row items-center gap-4">
        <Users className="h-8 w-8 text-purple-500 flex-shrink-0" />
        <div>
          <p className="font-semibold">Business plan includes team collaboration</p>
          <p className="text-sm text-muted-foreground">Invite up to 2 colleagues with role-based access. Team members can view dashboards and manage ad accounts — billing stays with you as the account owner.</p>
        </div>
        {currentTier !== 'business' && (
          <Button className="flex-shrink-0" onClick={() => handleSubscribe('business')} disabled={loading}>
            {loading && selectedPlan === 'business' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Get Business
          </Button>
        )}
      </div>

      {/* FAQ */}
      <div className="max-w-2xl mx-auto">
        <h2 className="text-xl font-semibold text-center mb-6">Frequently asked questions</h2>
        <div className="space-y-4">
          {[
            { q: 'Can I switch plans anytime?', a: 'Yes — upgrade instantly, downgrades take effect at the end of your billing period.' },
            { q: 'What happens to my data if I downgrade?', a: 'Your data is preserved. Features beyond your plan limit (e.g. extra stores) become read-only until you upgrade again.' },
            { q: 'How do team members work?', a: 'On the Business plan you can invite 2 colleagues by email. They get their own login with role-based access. You control billing and team membership.' },
            { q: 'Is there a contract?', a: 'No contracts — cancel anytime with one click from your billing portal.' },
            { q: 'Do you offer annual billing?', a: 'Yes — annual plans get 2 months free. Contact support to switch.' },
            { q: 'Is HapiGuard included in all plans?', a: 'Yes — every paid plan includes HapiGuard click protection. Install the free WordPress plugin and connect your RevHapi account. Starter gets country geo-blocking. Pro adds device ID, OS, and IP hash blocking plus email alerts for anomalies.' },
          ].map((faq, i) => (
            <div key={i} className="p-4 rounded-xl bg-muted/50">
              <p className="font-medium text-sm">{faq.q}</p>
              <p className="text-sm text-muted-foreground mt-1">{faq.a}</p>
            </div>
          ))}
        </div>
        <p className="text-center text-sm text-muted-foreground mt-8">
          Questions? <a href="mailto:support@revhapi.com" className="text-primary hover:underline">Contact support</a>
        </p>
      </div>
    </div>
  );
}
